import type { FC, ReactNode } from 'react';
import React from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import { expVal } from '@confluence/feature-experiments';
import { canAddCommentsFromOperations } from '@confluence/entity-operations/entry-points/canAddCommentsFromOperations.ts';

import type { ViewPageCommentsPanelPermission_content$key } from './__generated__/ViewPageCommentsPanelPermission_content.graphql.ts';

export const ViewPageCommentsPanelPermissionFragment: GraphQLTaggedNode = graphql`
	fragment ViewPageCommentsPanelPermission_content on Content {
		id
		operations {
			operation
			targetType
		}
	}
`;

type Props = {
	contentFragmentRef?: ViewPageCommentsPanelPermission_content$key | null;
	children: (preloadedCanAddComments?: boolean) => ReactNode;
};

const ViewPageCommentsPanelPermissionWithFragment: FC<{
	contentFragmentRef: NonNullable<Props['contentFragmentRef']>;
	children: Props['children'];
}> = ({ contentFragmentRef, children }) => {
	const contentOperations = useFragment(
		ViewPageCommentsPanelPermissionFragment,
		contentFragmentRef,
	);

	return <>{children(canAddCommentsFromOperations(contentOperations.operations))}</>;
};

export const ViewPageCommentsPanelPermission: FC<Props> = ({ contentFragmentRef, children }) => {
	if (
		contentFragmentRef &&
		expVal('confluence_comments_panel_relay_content_ops', 'isEnabled', false)
	) {
		return (
			<ViewPageCommentsPanelPermissionWithFragment contentFragmentRef={contentFragmentRef}>
				{children}
			</ViewPageCommentsPanelPermissionWithFragment>
		);
	}

	return <>{children()}</>;
};
